import axios from 'axios'
import { message } from 'ant-design-vue'

// 懒加载 store 和 router，避免循环依赖导致模块初始化时崩溃
function getStore() {
  return require('@/store').default
}
function getRouter() {
  return require('@/router').default
}

function normalizeTenantCode(input) {
  return (input || '').toString().trim()
}

function encodeTenantCodeForHeader(code) {
  const v = normalizeTenantCode(code)
  if (!v) return ''
  // Header 仅允许安全字符，中文编码后传输，后端再 urldecode
  return encodeURIComponent(v)
}

const request = axios.create({
  baseURL: 'backend/index.php/api',
  timeout: 30000
})

// 请求拦截：自动附加Token和租户信息
request.interceptors.request.use(config => {
  // 判断当前是否处于平台管理后台（tenant-admin）路由下
  const isTenantAdminPage = (
    typeof window !== 'undefined' &&
    window.location &&
    (window.location.hash.indexOf('/tenant-admin/') !== -1 ||
     window.location.pathname.indexOf('/tenant-admin/') !== -1)
  )
  // 判断当前是否处于独立商城管理端（门店端）路由下
  // 门店端与总部端复用同一批 /mall/* 接口，只能靠页面路径区分身份
  const isStoreAdminPage = (
    typeof window !== 'undefined' &&
    window.location &&
    (window.location.hash.indexOf('/store-admin/') !== -1 ||
     window.location.pathname.indexOf('/store-admin/') !== -1)
  )
  const isStoreAdminApi = !!(config.url && config.url.indexOf('/mall/store-auth') !== -1)

  // 平台管理后台的所有API请求使用独立的 tenant_admin_token
  if (isTenantAdminPage || (config.url && config.url.startsWith('/tenant_admin'))) {
    const tenantAdminToken = localStorage.getItem('tenant_admin_token')
    if (tenantAdminToken) {
      config.headers['Authorization'] = `Bearer ${tenantAdminToken}`
    }
  } else if (isStoreAdminPage || isStoreAdminApi) {
    // 门店端只发门店管理端 token：
    // 绝不能带租户业务端 token（会被后端判为总部身份，导致跨门店越权）
    const storeAdminToken = localStorage.getItem('store_admin_token')
    if (storeAdminToken) {
      config.headers['Authorization'] = `Bearer ${storeAdminToken}`
    }
    // 门店端同样需要租户编码（后端据此定位租户）
    // 优先用门店端自己的公司编号，避免与 PC 端的 current_tenant_code 混淆
    const tc = normalizeTenantCode(localStorage.getItem('store_admin_tenant_code'))
      || normalizeTenantCode(localStorage.getItem('current_tenant_code'))
      || 'default'
    config.headers['X-Tenant-Code'] = encodeTenantCodeForHeader(tc) || 'default'
  } else {
    try {
      const store = getStore()
      const token = store.getters.token
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
      // 添加租户编码Header（用于多租户识别）
      const userInfo = store.getters.userInfo
      const userTenantCode = normalizeTenantCode(userInfo ? userInfo.tenant_code : '')
      const localTenantCode = normalizeTenantCode(localStorage.getItem('current_tenant_code'))
      const tenantCode = userTenantCode || localTenantCode || 'default'
      config.headers['X-Tenant-Code'] = encodeTenantCodeForHeader(tenantCode) || 'default'
    } catch (e) {
      // store 未就绪时使用 localStorage
      const localTenantCode = normalizeTenantCode(localStorage.getItem('current_tenant_code')) || 'default'
      config.headers['X-Tenant-Code'] = encodeTenantCodeForHeader(localTenantCode) || 'default'
    }
  }
  return config
}, err => Promise.reject(err))

// 响应拦截：统一处理错误
request.interceptors.response.use(
  res => {
    // blob 类型直接返回原始响应（用于文件下载）
    if (res.config.responseType === 'blob') {
      return res
    }
    const data = res.data
    // 租户超管API使用 code: 0 表示成功，其他API使用 code: 200 表示成功
    const isTenantAdminApi = res.config.url && res.config.url.startsWith('/tenant_admin')
    const successCode = isTenantAdminApi ? 0 : 200

    if (data.code !== successCode && data.code !== 0 && data.code !== 200) {
      // _silent 的请求（如回填可选关联名称）仅静默 reject，不弹错误提示
      if (!(res.config && res.config._silent)) {
        message.error(data.msg || data.message || '操作失败')
      }
      return Promise.reject(new Error(data.msg || data.message))
    }
    return data
  },
  err => {
    const silent = err.config && err.config._silent
    if (err.code === 'ECONNABORTED' && err.message && err.message.includes('timeout')) {
      if (!silent) message.error('网络超时，请稍后查看状态，确认未成功再重新提交')
      return Promise.reject(new Error('网络超时，请稍后查看状态'))
    }
    if (err.response) {
      const status = err.response.status
      if (status === 401) {
        // 区分 tenant-admin / 门店端 / PC端，各自清除对应token并跳转对应登录页
        const isTenantAdminRequest = (
          (err.config && err.config.url && err.config.url.startsWith('/tenant_admin')) ||
          (typeof window !== 'undefined' && window.location &&
            (window.location.hash.indexOf('/tenant-admin/') !== -1 ||
             window.location.pathname.indexOf('/tenant-admin/') !== -1))
        )
        // 门店端：只清门店端 token，
        // 若走 PC 分支会把租户业务端的登录态一起清掉（两个端互相踢下线）
        const isStoreAdminRequest = (
          (err.config && err.config.url && err.config.url.indexOf('/mall/store-auth') !== -1) ||
          (typeof window !== 'undefined' && window.location &&
            (window.location.hash.indexOf('/store-admin/') !== -1 ||
             window.location.pathname.indexOf('/store-admin/') !== -1))
        )
        if (isTenantAdminRequest) {
          localStorage.removeItem('tenant_admin_token')
          localStorage.removeItem('tenant_admin_info')
          try { getRouter().push('/tenant-admin/login') } catch (e) {}
        } else if (isStoreAdminRequest) {
          localStorage.removeItem('store_admin_token')
          localStorage.removeItem('store_admin_store')
          localStorage.removeItem('store_admin_tenant')
          localStorage.removeItem('store_admin_tenant_code')
          try { getRouter().push('/store-admin/login') } catch (e) {}
        } else {
          try { getStore().dispatch('logout') } catch (e) {}
          try { getRouter().push('/login') } catch (e) {}
        }
        if (!silent) message.error('登录已过期，请重新登录')
      } else if (status === 403) {
        if (!silent) message.error(err.response.data?.msg || '权限不足')
      } else if (status === 404) {
        if (!silent) message.error('请求的资源不存在')
      } else {
        if (!silent) message.error(err.response.data?.msg || '服务器错误')
      }
    } else {
      if (!silent) message.error('网络连接失败')
    }
    return Promise.reject(err)
  }
)

export default request
